import moment from "moment";
import { AvailableTemplate } from "../models/AvailableTemplates";
import { ProcessMessageIndexType, ProcessTemplateInput } from "../__generated__/globalTypes";
import { Instruction } from "../models/Instruction";
import { v4 as uuid } from "uuid";
import { UserProject } from "../models/UserProjects";
import { Company } from "../models/Companies";
import { FormInput, UserInput, UserInputs } from "src/models/UserInputs";
import { ListState } from "src/models/TemplateCreationList";

export const DefaultCompany: Company = { __typename: "Company", id: "global", name: "global" };

export type Dictionary<T> = { [key: string]: T };

export function initTemplateInput() {
    const guid = uuid().replace(/-/g, "");
    return {
        companySysId: "global",
        processId: guid,
        name: "",
        description: "",
        instructions: [],
        outputs: {},
        processInputs: {}
    } as ProcessTemplateInput;
}

export function validateAllInputsMapped(
    masterList: Dictionary<ListState>,
    instructions: Instruction[],
    processInputs: Dictionary<UserInput>
): [boolean, string | undefined] {
    for (const instruction of instructions) {
        const instructionProjectId = instruction.projectId;
        if (!masterList[instructionProjectId]) {
            return [false, `Instruction user inputs not mapped.`];
        }
        const variableList = masterList[instructionProjectId];

        for (const variable of variableList.manual) {
            if (!variable.mapsTo) {
                return [false, `Manually set variable ${variable.original} is not mapped.`];
            }
            if (!processInputs[variable.mapsTo]) {
                console.log("in the annoying case");
                return [
                    false,
                    `Manually set variable ${variable.original} does not have a valid mapping in process inputs.`
                ];
            }
        }

        for (const variable of variableList.dependency) {
            if (!variable.mapsTo) {
                return [false, `Dependecy set variable ${variable.original} is not mapped.`];
            }
        }

        for (const variable of variableList.hardcode) {
            if (!!!variable.value) {
                return [false, `Hardcode set variable ${variable.original} does not have a value.`];
            }
        }
    }
    return [true, undefined];
}

export function createTemplate(
    companySysId: any,
    name: string,
    description: string,
    instructions: Array<Instruction>,
    processInputs: UserInputs,
    inputSourceList: Dictionary<ListState>,
    availableProjects: UserProject[]
) {
    setAdditionalInstructionVariables(instructions, inputSourceList, availableProjects);
    const guid = uuid().replace(/-/g, "");
    return {
        companySysId: companySysId,
        processId: guid,
        name: name,
        description: description,
        instructions: instructions,
        processInputs: processInputs
    } as ProcessTemplateInput;
}

function setAdditionalInstructionVariables(
    instructions: Array<Instruction>,
    inputSourceList: Dictionary<ListState>,
    availableProjects: UserProject[]
) {
    instructions.forEach(i => {
        i.outputs = getInstructionOutputs(i, availableProjects);
        i.userInputs = getInstructionUserInputs(i, inputSourceList);
    });
}

function getInstructionOutputs(instruction: Instruction, availableProjects: UserProject[]) {
    const outputs: Dictionary<string> = {};
    const instructionProject = availableProjects.find(aP => aP.projectId === instruction.projectId);
    if (instructionProject) {
        Object.keys(instructionProject.outputs).forEach(o => (outputs[o] = `$ref['runtime'].outputs.${o}`));
    }
    return outputs;
}

function getInstructionUserInputs(instruction: Instruction, inputSourceList: Dictionary<ListState>) {
    const userInputs: Dictionary<{ value: string }> = {};
    //!todo use instructionId instead
    const manual = inputSourceList[instruction.projectId].manual;
    const hardcoded = inputSourceList[instruction.projectId].hardcode;
    const dependecy = inputSourceList[instruction.projectId].dependency;
    manual.forEach(m => {
        userInputs[m.original] = { value: `$.processInputs.${m.mapsTo}.value` };
    });

    dependecy.forEach(d => {
        userInputs[d.original] = {
            value: `$.instructions[?(@.instructionId == '${d.source}')].outputs.${d.mapsTo}`
        };
    });

    //!todo: add other types besides primative string
    hardcoded.forEach(h => {
        userInputs[h.original] = { value: h.value ? h.value : "" };
    });
    return userInputs;
}

export function transformToTemplateInput(template: AvailableTemplate) {
    return {
        companySysId: template.companySysId,
        processId: template.processId,
        name: template.name ?? "",
        description: template.description ?? "",
        instructions: [...template.instructions],
        outputs: template.outputs ? { ...template.outputs } : {}
    } as ProcessTemplateInput;
}

export function insertInstruction(userProject: UserProject, instructions: Instruction[]) {
    const updatedInstruction = [...instructions];
    const { projectId, id: projectKey, name } = userProject;
    const guid = uuid().replace(/-/g, "");
    updatedInstruction.push({
        instructionId: guid,
        indexType: "project",
        projectId,
        projectKey,
        alias: name,
        display: { label: name, order: 10 * (instructions.length + 1) },
        dependsOn: [],
        precondition: [],
        outputs: {}
    });

    return updatedInstruction.sort(sortByDisplayOrder);
}

export function swapInstructions(instructions: Instruction[], baseIndex: number) {
    const curInstruction = { ...instructions[baseIndex + 1] };
    const prevInstruction = { ...instructions[baseIndex] };
    const { order } = { ...curInstruction.display };
    curInstruction.display = { label: curInstruction.display.label, order: prevInstruction.display.order };
    prevInstruction.display = { label: prevInstruction.display.label, order };
    instructions.splice(baseIndex, 2, curInstruction, prevInstruction);
}

export function updateInstructionProp(instruction: Instruction, key: string, value: string): Instruction {
    const toBeUpdated = { ...instruction };
    switch (key) {
        case "alias":
            toBeUpdated.alias = value;
            break;
        case "display":
            toBeUpdated.display.label = value;
            break;
        default:
            console.log(`no element named ${key} in instruction`);
    }
    return toBeUpdated;
}

export function updateInstructionArray(instruction: Instruction, key: string, value: string[]) {
    const toBeUpdated = { ...instruction };
    switch (key) {
        case "precondition":
            toBeUpdated.precondition = [...value];
            break;
        case "dependsOn":
            toBeUpdated.dependsOn = [...value];
            break;
        default:
            console.log(`no element named ${key} in instruction`);
    }
    return toBeUpdated;
}

export function updateInstruction(key: string, value: string | string[], instruction: Instruction) {
    if (key === "alias" || key === "display") {
        return updateInstructionProp(instruction, key, value as string);
    }
    return updateInstructionArray(instruction, key, value as string[]);
}

export function sortByDisplayOrder(one: Instruction, other: Instruction) {
    const oneOrder = one.display.order;
    const otherOrder = other.display.order;
    return oneOrder - otherOrder;
}

export function removeDependency(instructions: Instruction[], instructionId: string) {
    const updatedInstructions: Instruction[] = [];
    for (const instruction of instructions) {
        const dependsOn = instruction.dependsOn.filter(d => d !== instructionId);
        updatedInstructions.push({ ...instruction, dependsOn } as Instruction);
    }
    return updatedInstructions;
}

export const relativeTime = (d: number | Date) => {
    const now = moment(new Date());
    const m = moment(d);
    return now.diff(m, "seconds") < 5 ? "just now" : m.fromNow();
};

export const localTime = (d: number | Date) => moment(d).format("ll LTS");
export type TemplateProject = UserProject & {
    visible: boolean;
};

export function createProcessRequestInput(
    processInputs: Dictionary<FormInput>,
    template: AvailableTemplate,
    action: ProcessAction
) {
    return {
        processInputs: processInputs,
        requestSysId: uuid(),
        companySysId: template.companySysId,
        processId: template.processId,
        indexType: getIndexFromActionType(action),
        instructions: template.instructions
    };
}

export function getIndexFromActionType(action: ProcessAction) {
    switch (action) {
        case "run":
            return ProcessMessageIndexType.procrequest;
        case "terminate":
            return ProcessMessageIndexType.procterminate;
        case "approve":
            return ProcessMessageIndexType.procautoevent;
    }
}

// export function localTime(timestamp: string | undefined | null) {
//     return timestamp ? moment(timestamp).format("LLLL") : "";
// }

export type ProcessAction = "run" | "approve" | "terminate";
